import { v4 as uuidv4 } from 'uuid';
import { classifyError, ERROR_KIND } from './classifyError';
import {
  registerNotificationCallback,
  clearNotificationCallback,
} from './notificationCallbacks';
import {
  showToast,
  showActionModal,
  hideActionModal,
} from '../store/notificationSlice';

const toastVariantForKind = (kind) => {
  if (kind === ERROR_KIND.NETWORK) return 'network';
  if (kind === ERROR_KIND.AUTH) return 'warning';
  if (kind === ERROR_KIND.BLOCKED) return 'info';
  return 'error';
};

/**
 * @param {import('@reduxjs/toolkit').Store} store
 */
export const createNotifier = (store) => {
  const dispatch = store.dispatch;

  const toast = ({
    type = 'error',
    title = '',
    message,
    duration,
    actionLabel,
    onAction,
  }) => {
    const actionCallbackId = onAction
      ? registerNotificationCallback(uuidv4(), onAction)
      : null;

    dispatch(showToast({
      type,
      title,
      message,
      duration,
      actionLabel,
      actionCallbackId,
    }));
  };

  const actionModal = ({
    title,
    message,
    details,
    onRetry,
  }) => {
    const retryCallbackId = onRetry
      ? registerNotificationCallback(uuidv4(), onRetry)
      : null;

    dispatch(showActionModal({
      title,
      message,
      details,
      retryCallbackId,
    }));
  };

  const closeActionModal = () => {
    const { retryCallbackId } = store.getState().notifications.actionModal;
    clearNotificationCallback(retryCallbackId);
    dispatch(hideActionModal());
  };

  const fromError = (error, options = {}) => {
    const classified = classifyError(error, options);
    const {
      forceToast = false,
      forceModal = false,
      title,
      message,
      onRetry,
    } = options;

    const resolvedTitle = title || classified.title;
    const resolvedMessage = message || classified.message;

    if (!classified.shouldShowGlobally && !forceToast && !forceModal) {
      return classified;
    }

    const useModal = forceModal && !forceToast;

    if (useModal) {
      actionModal({
        title: resolvedTitle,
        message: resolvedMessage,
        onRetry: classified.canRetry ? onRetry : undefined,
      });
      return classified;
    }

    toast({
      type: toastVariantForKind(classified.kind),
      title: resolvedTitle,
      message: resolvedMessage,
      actionLabel: classified.canRetry && onRetry ? 'Erneut versuchen' : undefined,
      onAction: classified.canRetry ? onRetry : undefined,
      duration: classified.kind === ERROR_KIND.AUTH ? 7000 : 5000,
    });

    return classified;
  };

  return {
    toast,
    actionModal,
    closeActionModal,
    fromError,
    success: (message, title = '') => toast({ type: 'success', title, message, duration: 4000 }),
    info: (message, title = '') => toast({ type: 'info', title, message, duration: 4000 }),
    network: (message, title = 'Verbindungsproblem') => toast({
      type: 'network',
      title,
      message,
      duration: 6000,
    }),
  };
};

let notifierSingleton = null;

export const initNotifier = (store) => {
  notifierSingleton = createNotifier(store);
  return notifierSingleton;
};

export const getNotifier = () => {
  if (!notifierSingleton) {
    throw new Error('Notifier not initialized');
  }
  return notifierSingleton;
};
